import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import classNames from 'clsx';
import assetUrl from 'src/common/utils/assetUrl';

const defaultImageSrc = assetUrl('assets/img/default-item-v2.png');

/**
 * @param id {string} unique element id
 * @param dataTestId {string} unique test id for automation tests
 * @param style {object} CSS styles to be applied to element
 * @param containerClassName {string} class names to be applied to LazyLoad container
 * @param className {string} class names to be applied to element
 * @param src {string} URL that resolves image file
 * @param alt {string} text description of image file
 * @param placeholder {string} URL that resolves placeholder image file
 * @param renderImageWithCss {boolean} flag that enables CSS image loading over img tag, when true
 */
const ImageWithDefault = ({
  id,
  dataTestId,
  style = {},
  containerClassName,
  className,
  src,
  alt,
  placeholder,
  renderImageWithCss = false,
}) => {
  const [imageSrc, setImageSrc] = useState(src);

  useEffect(() => {
    setImageSrc(src);
  }, [src]);

  const placeholderImageSrc = placeholder || defaultImageSrc;
  const placeholderImg = (
    <img
      id={`${id}-placeholder`}
      data-testid={`${dataTestId}-placeholder`}
      className={className}
      style={style}
      src={placeholderImageSrc}
      alt={alt}
    />
  );

  return (
    <LazyLoad
      placeholder={placeholderImg}
      offset={100}
      once
      overflow
      className={containerClassName}
    >
      {renderImageWithCss && (
        <div
          id={`${id}-div`}
          data-testid={`${dataTestId}-div`}
          className={className}
          style={{ ...style, backgroundImage: `url('${imageSrc}')` }}
          role="img"
          aria-label={alt}
        />
      )}
      <img
        id={id}
        data-testid={dataTestId}
        className={classNames(className, { hide: renderImageWithCss })}
        style={style}
        src={imageSrc}
        alt={alt}
        onError={() => setImageSrc(placeholderImageSrc)}
      />
    </LazyLoad>
  );
};

ImageWithDefault.propTypes = {
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  style: PropTypes.object,
  containerClassName: PropTypes.string,
  className: PropTypes.string,
  alt: PropTypes.string,
  placeholder: PropTypes.string,
  src: PropTypes.string,
  renderImageWithCss: PropTypes.bool,
};

export default ImageWithDefault;
